import type { LegalDocPair } from '../types'

/**
 * Política de derechos de autor (proceso de notificación y contranotificación
 * de la ley DMCA de Estados Unidos).
 *
 * OJO al publicar: para acogerse al puerto seguro de la DMCA hay que registrar
 * un agente designado ante la Oficina de Derechos de Autor de EE. UU. Mientras
 * no se registre, esta política describe el proceso pero no da esa protección.
 */
export const COPYRIGHT: LegalDocPair = {
  en: {
    title: 'Copyright Policy',
    summary: 'How to report content that infringes your copyright, and how we handle it.',
    intro: [
      'We respect the intellectual property rights of others and expect our users to do the same. This Copyright Policy is part of the [Terms of Service](doc:terms) and explains how to report content on a Site hosted by véltica that you believe infringes your copyright, and how a user can respond. We follow the notice-and-takedown process of the United States Digital Millennium Copyright Act (“DMCA”).',
    ],
    sections: [
      {
        title: 'Reporting infringement',
        blocks: [
          'If you are the owner of a copyright, or are authorized to act for the owner, and you believe content on a Site hosted by véltica infringes it, send a written notice to our designated agent with all of the following:',
          {
            ordered: [
              'your physical or electronic signature;',
              'a description of the copyrighted work you claim has been infringed;',
              'the address (URL) of the material you claim infringes, and enough information for us to find it;',
              'your name, postal address, telephone number and email address;',
              'a statement that you have a good-faith belief that the use is not authorized by the copyright owner, its agent or the law; and',
              'a statement, under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorized to act for the owner.',
            ],
          },
          'Send it to our designated agent: {operator}, {location}, email {email}, subject line “DMCA notice”. Notices that do not contain all of this information may not be processed. Be aware that under the law you may be liable for damages if you knowingly misrepresent that content is infringing.',
        ],
      },
      {
        title: 'What we do with a notice',
        blocks: [
          'When we receive a valid notice, we may remove or disable access to the material and we will notify the user who published it, including by forwarding the notice, which includes your contact information.',
        ],
      },
      {
        title: 'Counter-notice',
        blocks: [
          'If you are a user and believe your content was removed by mistake or misidentification, you may send a counter-notice to the same address, with:',
          {
            ordered: [
              'your physical or electronic signature;',
              'identification of the material that was removed and where it appeared before removal;',
              'a statement, under penalty of perjury, that you have a good-faith belief the material was removed by mistake or misidentification; and',
              'your name, address and telephone number, and a statement that you consent to the jurisdiction of the federal court for the district where you are located (or, if you are outside the United States, of the courts in {county} County, California) and that you will accept service of process from the person who sent the notice.',
            ],
          },
          'If we receive a valid counter-notice, we will send it to the person who filed the original notice. If that person does not tell us within 10 business days that they have filed a court action, we may restore the material within 10 to 14 business days after receiving the counter-notice.',
        ],
      },
      {
        title: 'Repeat infringers',
        blocks: [
          'In appropriate circumstances, we will terminate the accounts of users who repeatedly infringe the copyright of others. We may also remove content or terminate accounts for other infringements of intellectual property.',
        ],
      },
      {
        title: 'Other intellectual property claims',
        blocks: [
          'For trademark and other intellectual property complaints, write to {email} with the details of your claim and the address of the content. We may remove content and share your report with the user, at our discretion and as the law requires.',
        ],
      },
    ],
  },

  es: {
    title: 'Política de derechos de autor',
    summary: 'Cómo denunciar contenido que infringe tus derechos de autor y cómo lo gestionamos.',
    intro: [
      'Respetamos los derechos de propiedad intelectual de los demás y esperamos que nuestros usuarios también. Esta Política de derechos de autor forma parte de los [Términos de servicio](doc:terms) y explica cómo denunciar el contenido de un Sitio alojado en véltica que creas que infringe tus derechos de autor, y cómo puede responder un usuario. Seguimos el proceso de notificación y retirada de la ley de Derechos de Autor del Milenio Digital de Estados Unidos («DMCA»).',
    ],
    sections: [
      {
        title: 'Denunciar una infracción',
        blocks: [
          'Si eres titular de un derecho de autor, o estás autorizado a actuar por el titular, y crees que el contenido de un Sitio alojado en véltica lo infringe, envía una notificación escrita a nuestro agente designado con todo lo siguiente:',
          {
            ordered: [
              'tu firma física o electrónica;',
              'una descripción de la obra protegida que consideras infringida;',
              'la dirección (URL) del material que consideras infractor y la información suficiente para que lo encontremos;',
              'tu nombre, dirección postal, teléfono y correo electrónico;',
              'una declaración de que crees de buena fe que el uso no está autorizado por el titular, su agente ni la ley; y',
              'una declaración, bajo pena de perjurio, de que la información de tu notificación es exacta y de que eres el titular del derecho de autor o estás autorizado a actuar por él.',
            ],
          },
          'Envíala a nuestro agente designado: {operator}, {location}, correo {email}, con el asunto «Notificación DMCA». Las notificaciones que no contengan toda esta información pueden no tramitarse. Ten en cuenta que, según la ley, puedes responder por daños si afirmas a sabiendas que un contenido es infractor sin que lo sea.',
        ],
      },
      {
        title: 'Qué hacemos con una notificación',
        blocks: [
          'Cuando recibimos una notificación válida, podemos retirar el material o desactivar el acceso a él y avisaremos al usuario que lo publicó, incluso reenviándole la notificación, que incluye tus datos de contacto.',
        ],
      },
      {
        title: 'Contranotificación',
        blocks: [
          'Si eres usuario y crees que tu contenido se retiró por error o por una identificación equivocada, puedes enviar una contranotificación a la misma dirección, con:',
          {
            ordered: [
              'tu firma física o electrónica;',
              'la identificación del material retirado y de dónde aparecía antes de retirarse;',
              'una declaración, bajo pena de perjurio, de que crees de buena fe que el material se retiró por error o por una identificación equivocada; y',
              'tu nombre, dirección y teléfono, y una declaración de que aceptas la jurisdicción del tribunal federal del distrito donde estés (o, si estás fuera de Estados Unidos, de los tribunales del condado de {county}, California) y de que aceptarás el emplazamiento de quien envió la notificación.',
            ],
          },
          'Si recibimos una contranotificación válida, se la enviaremos a quien presentó la notificación original. Si esa persona no nos dice en 10 días hábiles que ha presentado una acción judicial, podemos restaurar el material entre 10 y 14 días hábiles después de recibir la contranotificación.',
        ],
      },
      {
        title: 'Infractores reincidentes',
        blocks: [
          'En las circunstancias que procedan, cerraremos las cuentas de los usuarios que infrinjan de forma repetida los derechos de autor de otros. También podemos retirar contenido o cerrar cuentas por otras infracciones de propiedad intelectual.',
        ],
      },
      {
        title: 'Otras reclamaciones de propiedad intelectual',
        blocks: [
          'Para quejas de marcas y de otra propiedad intelectual, escribe a {email} con los detalles de tu reclamación y la dirección del contenido. Podemos retirar contenido y compartir tu denuncia con el usuario, a nuestra discreción y como exija la ley.',
        ],
      },
    ],
  },
}
